import React, { useState, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import { X, Sparkles, Loader, History, Save, RotateCcw, Lock, AlertCircle } from 'lucide-react'
import type { GeneratedPolicy } from '../lib/policy-generator-types'

interface PolicyEditorProps {
  policy: GeneratedPolicy
  sessionId: string
  onClose: () => void
  onUpdate: (updatedPolicy: GeneratedPolicy) => void
}

interface EditLockInfo {
  locked: boolean
  canEdit: boolean
  lockedBy?: string
  lockedByName?: string
  lockedAt?: string
  expiresAt?: string
  isOwnLock?: boolean
}

export default function PolicyEditor({ policy, sessionId, onClose, onUpdate }: PolicyEditorProps) {
  const currentVersion = policy.versions[policy.versions.length - 1]
  const [content, setContent] = useState(currentVersion?.markdownContent || '')
  const [editPrompt, setEditPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [viewMode, setViewMode] = useState<'preview' | 'markdown'>('preview')
  const [showHistory, setShowHistory] = useState(false)

  // Edit lock state
  const [lockInfo, setLockInfo] = useState<EditLockInfo>({ locked: false, canEdit: true })
  const [lockChecking, setLockChecking] = useState(true)
  const lockRenewalTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Early return if no current version exists
  if (!currentVersion) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-700 mb-4">No policy version available.</p>
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  const quickActions = [
    'Make this more concise',
    'Add more detail',
    'Simplify the language',
    'Add examples',
    'Make it more formal',
    'Add a section about incident response'
  ]

  // Acquire edit lock on mount
  useEffect(() => {
    const acquireLock = async () => {
      try {
        const response = await fetch('/api/edit-lock', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'policy',
            id: policy.id,
            userName: 'Current User' // TODO: Get actual user name from context
          })
        })

        const data = await response.json()

        if (response.ok && data.success) {
          // Lock acquired successfully
          setLockInfo({ locked: true, canEdit: true, isOwnLock: true })

          // Set up lock renewal timer (renew every 3 minutes, lock expires in 5)
          lockRenewalTimerRef.current = setInterval(async () => {
            try {
              await fetch('/api/edit-lock', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  type: 'policy',
                  id: policy.id,
                  userName: 'Current User'
                })
              })
              console.log('[Edit Lock] Lock renewed')
            } catch (error) {
              console.error('[Edit Lock] Failed to renew lock:', error)
            }
          }, 3 * 60 * 1000) // 3 minutes
        } else if (response.status === 409) {
          // Lock held by someone else
          setLockInfo({
            locked: true,
            canEdit: false,
            lockedBy: data.lockedBy,
            lockedByName: data.lockedByName,
            lockedAt: data.lockedAt,
            expiresAt: data.expiresAt,
            isOwnLock: false
          })
        } else {
          // Other error - allow editing but warn
          console.warn('[Edit Lock] Failed to acquire lock, allowing editing anyway:', data)
          setLockInfo({ locked: false, canEdit: true })
        }
      } catch (error) {
        console.error('[Edit Lock] Error acquiring lock:', error)
        // On error, allow editing
        setLockInfo({ locked: false, canEdit: true })
      } finally {
        setLockChecking(false)
      }
    }

    acquireLock()

    // Cleanup: release lock on unmount
    return () => {
      // Clear renewal timer
      if (lockRenewalTimerRef.current) {
        clearInterval(lockRenewalTimerRef.current)
      }

      // Release lock (fire and forget)
      fetch(`/api/edit-lock?type=policy&id=${policy.id}`, {
        method: 'DELETE'
      }).catch(err => console.error('[Edit Lock] Failed to release lock:', err))
    }
  }, [policy.id])

  const handleAiEdit = async (prompt: string) => {
    setLoading(true)
    setEditPrompt(prompt)

    try {
      const response = await fetch('/api/policy-generator/edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          policyId: policy.id,
          editPrompt: prompt,
          currentContent: content
        })
      })

      if (!response.ok) {
        throw new Error('Failed to edit policy')
      }

      const data = await response.json()
      setContent(data.newVersion.markdownContent)
      onUpdate(data.policy)

      // Clear prompt after successful edit
      setEditPrompt('')
    } catch (error) {
      console.error('AI edit failed:', error)
      alert('Failed to edit policy. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleVersionSwitch = (versionNumber: number) => {
    const version = policy.versions.find(v => v.version === versionNumber)
    if (version) {
      setContent(version.markdownContent)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-7xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{policy.policyName}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Version {currentVersion.version} • Last updated {new Date(currentVersion.createdAt).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center"
            >
              <History className="h-4 w-4 mr-1" />
              History ({policy.versions.length})
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Lock Status Banner */}
        {!lockChecking && !lockInfo.canEdit && lockInfo.locked && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
            <div className="flex items-center space-x-2 text-yellow-800">
              <Lock className="h-5 w-5" />
              <div>
                <p className="font-medium">
                  This policy is currently being edited by {lockInfo.lockedByName || lockInfo.lockedBy}
                </p>
                <p className="text-sm text-yellow-700">
                  You can view the policy but cannot make changes. The lock will expire {lockInfo.expiresAt && new Date(lockInfo.expiresAt).toLocaleTimeString()}.
                </p>
              </div>
            </div>
          </div>
        )}

        {lockChecking && (
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
            <div className="flex items-center space-x-2 text-blue-800">
              <Loader className="h-5 w-5 animate-spin" />
              <p>Checking edit permissions...</p>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-hidden flex">
          {/* Version History Sidebar */}
          {showHistory && (
            <div className="w-64 border-r border-gray-200 overflow-y-auto p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Version History</h3>
              <div className="space-y-2">
                {[...policy.versions].reverse().map((version) => (
                  <button
                    key={version.version}
                    onClick={() => handleVersionSwitch(version.version)}
                    className={`
                      w-full text-left p-3 rounded border transition-colors
                      ${version.version === currentVersion.version
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                      }
                    `}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">v{version.version}</span>
                      <span className={`
                        px-2 py-0.5 text-xs rounded
                        ${version.changeType === 'initial' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}
                      `}>
                        {version.changeType}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">{version.changeSummary}</p>
                    <p className="text-xs text-gray-500">{new Date(version.createdAt).toLocaleDateString()}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* View Toggle */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex space-x-2">
                <button
                  onClick={() => setViewMode('preview')}
                  className={`px-4 py-2 text-sm rounded ${
                    viewMode === 'preview'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Preview
                </button>
                <button
                  onClick={() => setViewMode('markdown')}
                  className={`px-4 py-2 text-sm rounded ${
                    viewMode === 'markdown'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Markdown
                </button>
              </div>
            </div>

            {/* Content Display */}
            <div className="flex-1 overflow-y-auto p-6">
              {viewMode === 'preview' ? (
                <div className="prose prose-blue max-w-none">
                  <ReactMarkdown>{content}</ReactMarkdown>
                </div>
              ) : (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  disabled={!lockInfo.canEdit}
                  className="w-full h-full font-mono text-sm border border-gray-300 rounded p-4 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:text-gray-600 disabled:cursor-not-allowed"
                />
              )}
            </div>

            {/* AI Edit Panel */}
            <div className="border-t border-gray-200 p-4">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Sparkles className="h-4 w-4 inline mr-1" />
                  AI-Powered Editing
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !loading && editPrompt && lockInfo.canEdit && handleAiEdit(editPrompt)}
                    placeholder="e.g., 'Add a section about data retention' or 'Make this more concise'"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed"
                    disabled={loading || !lockInfo.canEdit}
                  />
                  <button
                    onClick={() => handleAiEdit(editPrompt)}
                    disabled={loading || !editPrompt || !lockInfo.canEdit}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                        Editing...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Edit
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <p className="text-xs text-gray-600 mb-2">Quick actions:</p>
                <div className="flex flex-wrap gap-2">
                  {quickActions.map((action) => (
                    <button
                      key={action}
                      onClick={() => handleAiEdit(action)}
                      disabled={loading || !lockInfo.canEdit}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {action}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
